import { findTitle, track, useTracking } from '../../utils/tracking';
import React, { useMemo } from 'react';
import { LinkObfuscated } from '../obfuscation/LinkObfuscated';

export const getLocationInPage = ({
  section,
  index,
}: {
  section?: string;
  index?: number | string;
}): string => {
  let locationInPage = index || '0';

  if (section) locationInPage = section + '_' + locationInPage;
  return locationInPage.toString();
};

const getUrlDestination = (url?: string) => {
  if (url?.startsWith('http')) return url;
  if (url?.startsWith('/')) return 'https://payfit.com' + url;
  return url;
};

export const getCtaPropsTracking = ({
  urlDestination,
  label,
  trackingPage,
  trackingZone,
  dataCtaId,
}) => {
  const trackProperties = {
    navigationPath: `${label} | ${trackingPage.page} | ${trackingZone} | ${
      urlDestination || 'button'
    } | ${dataCtaId}`,
    url: window?.document?.location?.href,
  };
  return trackProperties;
};

export const ButtonTracking = ({
  component: Component,
  index,
  section,
  children,
  event,
  labelTracking,
  trackEvent,
  ...props
}: {
  component: any;
  index?: string | number;
  section?: string;
  children?: any;
  event?: string;
  labelTracking?: string;
  trackEvent?: string;
  to?: string;
  href?: string;
  onClick?: (any) => void;
  isObfuscated?: boolean;
  isDisabled?: boolean;
  [x: string]: any;
}) => {
  const { trackingZone, trackingPage } = useTracking();

  const { dataCtaId, label } = useMemo(() => {
    const label = labelTracking || findTitle(props, children) || 'NA';
    const dataCtaId = getLocationInPage({ section, index });
    return { dataCtaId, label };
  }, [labelTracking, props, children, section, index]);

  const handleClick = (e: any) => {
    if (
      typeof window !== 'undefined' &&
      (props?.to ||
        (props?.href &&
          (props.href.startsWith('http') || props.href.startsWith('/'))))
    ) {
      const urlDestination = getUrlDestination(props?.to || props?.href);
      const url = new URL(urlDestination as string);
      const params = Array.from(
        new URLSearchParams(url.search)?.entries(),
      )?.reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {});
      const hash = url.hash;
      track({
        segment: { event: trackEvent, properties: { params, hash, label } },
        gtm: {
          event: 'CTA Click',
          properties: getCtaPropsTracking({
            urlDestination,
            label,
            trackingPage,
            trackingZone,
            dataCtaId,
          }),
        },
      });
    }

    if (props?.onClick) {
      props.onClick(e);
    }
  };
  // Prepare function in urlParams
  let href: string | undefined;
  if (props?.href?.startsWith('?')) {
    const params = new URLSearchParams(props.href);
    const functionName = params.get('function');
    if (functionName) {
      href = `javascript:${functionName}()`;
    }
  } else if (!props?.to) {
    href = props.href;
  }

  if (props.component !== LinkObfuscated)
    delete props.isObfuscated;

  if (props.href)
    delete props.isDisabled;

  return (
    <Component
      {...props}
      onClick={handleClick}
      data-cta-id={dataCtaId}
      href={href}
    >
      {children && children}
    </Component>
  );
};
